const accessibility = {
  "id": "accessibility",
  "slug": "accessibility",
  "category": "Foundations",
  "navTitle": "Embedding Accessibility Across XE",
  "title": "Embedding Accessibility Across XE",
  "introduction": [
    "Accessibility at XE began as an effort to address gaps in individual products, but I recognized that fixing issues one project at a time would not create lasting change. I led the development of a broader accessibility practice that brought standards, training, testing, and accountability into everyday design and development work.",
    "The initiative began with XE’s highest-traffic digital experiences and later expanded across other Euronet companies following the acquisition."
  ],
  "challenge": [
    {
      "title": "Accessibility was addressed too late",
      "paragraphs": [
        "Like many organizations at the time, XE often identified accessibility issues after products had already been designed or built. Fixing them late made the work slower, more expensive, and less consistent."
      ]
    },
    {
      "title": "A large and diverse audience",
      "paragraphs": [
        "Millions of people relied on XE’s digital products. The experiences needed to work for customers using keyboards, screen readers, high-contrast settings, and other assistive technologies."
      ]
    },
    {
      "title": "A mix of modern and legacy products",
      "paragraphs": [
        "XE’s newer React experiences could adopt accessible patterns more systematically, while older pages required gradual remediation and modernization."
      ]
    },
    {
      "title": "Accessibility needed shared ownership",
      "paragraphs": [
        "Lasting improvement could not depend on one person reviewing every interface. Designers, developers, QA teams, product leaders, and content contributors all needed to understand their role."
      ]
    }
  ],
  "process": [
    {
      "title": "Embedding accessibility into delivery",
      "paragraphs": [
        "I positioned accessibility as part of the normal product workflow, with standards and checks built into design, development, and QA."
      ],
      "bullets": [
        "Created a roadmap for bringing XE’s digital products into alignment with WCAG 2.0.",
        "Added accessibility checkpoints throughout design, development, and testing.",
        "Reviewed components, colour combinations, interaction states, keyboard behaviour, and screen-reader support.",
        "Connected accessibility requirements to the emerging design system so teams had approved patterns to use.",
        "Introduced tools such as pa11y, aXe, and Lighthouse to support automated testing alongside manual review."
      ],
      "closing": "This helped teams identify issues earlier and reduced the need to correct the same problems repeatedly."
    },
    {
      "title": "Building capability across teams",
      "paragraphs": [
        "I focused on giving designers, developers, and QA teams the knowledge and tools to address accessibility within their own work."
      ],
      "bullets": [
        "Delivered practical accessibility training across design, development, and QA.",
        "Explained the reasoning behind the standards so teams could apply them beyond a checklist.",
        "Clarified responsibilities and created shared review points across disciplines.",
        "Used findings from QA and customer feedback to improve guidance over time.",
        "Encouraged teams to treat accessibility as part of product quality rather than specialist approval."
      ],
      "closing": "This created broader ownership and made the practice less dependent on one person."
    },
    {
      "title": "Scaling the practice",
      "paragraphs": [
        "I prioritized XE’s highest traffic React experiences first, then used that work as the foundation for broader adoption across legacy products and Euronet companies."
      ],
      "bullets": [
        "Focused initial effort on React pages representing approximately 80% of XE.com traffic.",
        "Embedded accessible patterns into shared components so improvements carried across multiple experiences.",
        "Created a longer-term roadmap for updating legacy pages as they were redesigned or replaced.",
        "Shared XE’s standards, training materials, audit practices, and lessons with other Euronet teams.",
        "Helped shape a more centralized model for accessibility guidance and support across products and regions."
      ],
      "closing": "The practice expanded gradually from targeted improvements at XE into a broader centre-of-excellence model across Euronet."
    }
  ],
  "outcomes": [
    {
      "title": "Accessibility became part of delivery",
      "description": "Accessibility checks and standards were integrated into design, development, and QA rather than being left until the end of a project."
    },
    {
      "title": "Improved accessibility across core experiences",
      "description": "XE’s highest-traffic digital experiences were brought into alignment with WCAG 2.0, with remaining exceptions addressed through subsequent improvements and modernization."
    },
    {
      "title": "Stronger internal capability",
      "description": "Designers, developers, and QA teams had clearer standards, practical training, and tools they could apply directly within their work."
    },
    {
      "title": "Greater impact through reusable foundations",
      "description": "Embedding accessibility into shared components allowed improvements to benefit multiple pages and products rather than being implemented repeatedly."
    },
    {
      "title": "A model that extended beyond XE",
      "description": "The practices developed at XE informed a broader accessibility capability across Euronet companies and regions."
    }
  ],
  "reflection": [
    "This initiative reinforced that accessibility needs shared ownership. Designers, developers, QA, and product teams all had a role in making it part of everyday delivery.",
    "Once accessibility was considered earlier, issues were easier to prevent and less expensive to correct. Training, shared standards, and reusable components helped teams make better decisions without relying on one person to review everything."
  ]
};

export default accessibility;

